import { Link } from "@tanstack/react-router";
import { Sparkles, ShieldCheck, Eye, Truck, PenLine, CheckCircle2, Star, TrendingUp, MapPin, BadgeCheck } from "lucide-react";
import type { BuyOrder } from "@/lib/marketplace";
import { STATUS_META, displayId, gradeLabel, RAW_GRADE_META } from "@/lib/marketplace";
import { Price } from "@/lib/currency";
import { WishlistButton } from "@/components/WishlistButton";

function CardArt({ seed, imageUrl, isDemo }: { seed: number; imageUrl?: string; isDemo?: boolean }) {
  const hue = (seed * 47) % 360;
  return (
    <div
      className="relative aspect-[5/7] w-full overflow-hidden rounded-md border border-border"
      style={
        imageUrl
          ? undefined
          : {
              background: `linear-gradient(135deg, hsl(${hue} 55% 22%), hsl(${(hue + 60) % 360} 50% 14%))`,
            }
      }
    >
      {imageUrl ? (
        <>
          <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-center text-[10px] uppercase tracking-wider text-white/80 backdrop-blur">
            Buyer reference — not the seller's card
          </div>
        </>
      ) : (
        <>
          <div className="absolute inset-0 opacity-30" style={{
            background: `radial-gradient(circle at 30% 25%, hsl(${(hue + 30) % 360} 80% 60%), transparent 55%)`,
          }} />
          <div className="absolute inset-x-2 bottom-2 rounded bg-black/40 px-2 py-1 text-[10px] uppercase tracking-wider text-white/70 backdrop-blur">
            Card art
          </div>
        </>
      )}
      {isDemo && (
        <div className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 -rotate-6 bg-primary/90 py-1.5 text-center font-display text-sm font-extrabold uppercase tracking-[0.25em] text-primary-foreground shadow-lg ring-1 ring-black/20">
          Demo Order
        </div>
      )}
    </div>
  );
}

export function OrderCard({ order }: { order: BuyOrder }) {
  const s = STATUS_META[order.status];
  const isSold = order.status === "released" || order.status === "delivered";
  const isDemo = order.isDemo;

  const linkProps = isDemo
    ? { to: "/demo/$id" as const, params: { id: order.id } }
    : { to: "/orders/$id" as const, params: { id: order.id } };

  return (
    <Link
      {...linkProps}
      className="group panel relative flex flex-col gap-3 p-3 transition hover:border-primary/60 hover:shadow-[0_8px_32px_-12px_rgba(123,47,190,0.4)]"
    >
      {isDemo && (
        <span className="absolute left-2 top-2 z-20 inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-md">
          <Sparkles className="h-3 w-3" /> Demo Order
        </span>
      )}

      <div className="flex items-center justify-between">
        <span className="rounded-md bg-background/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {order.tcg}
        </span>
        <span className={`status-pill ${s.tone}`}>{s.label}</span>
      </div>

      <div className="relative">
        <CardArt seed={order.imageSeed} imageUrl={order.imageUrl} isDemo={order.isDemo} />
        {!isSold && (
          <div className="absolute right-2 top-2 z-20">
            <WishlistButton
              cardName={order.cardName}
              tcg={order.tcg}
              set={order.set}
              cardNumber={order.cardNumber}
              size="sm"
            />
          </div>
        )}
        {isSold && (
          <>
            <div className="pointer-events-none absolute inset-0 rounded-md bg-black/65" />
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 w-[88%] -translate-x-1/2 -translate-y-1/2 rounded-md py-3 text-center text-white shadow-lg"
              style={{ backgroundColor: "#1A6B3C" }}
            >
              <div className="flex items-center justify-center gap-1.5 font-display text-base font-extrabold">
                Order fulfilled <CheckCircle2 className="h-4 w-4" />
              </div>
            </div>
          </>
        )}
      </div>

      <div>
        <h3 className="line-clamp-1 font-display text-base font-semibold leading-tight group-hover:text-primary" title={displayId(order)}>
          {order.cardName}
        </h3>
        <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
          {order.condition === "graded" && order.gradingCo && order.minGrade != null ? gradeLabel(order.gradingCo, order.minGrade, order.gradeVariant) : `Raw${order.rawGrade ? ` · ${RAW_GRADE_META[order.rawGrade].full}` : ""}`} · #{order.shortId}
        </p>
        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
          {order.set} · #{order.cardNumber}
        </p>
      </div>

      {!isSold && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {order.condition === "graded" ? (
            <span className="inline-flex items-center gap-1 rounded bg-primary/15 px-1.5 py-0.5 text-primary">
              <ShieldCheck className="h-3 w-3" />
              {order.gradingCo && order.minGrade != null ? gradeLabel(order.gradingCo, order.minGrade, order.gradeVariant) : ""}+
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                <Sparkles className="h-3 w-3" /> Raw
              </span>
              {order.rawGrade && (
                <span
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-bold ${RAW_GRADE_META[order.rawGrade].tone}`}
                  title={RAW_GRADE_META[order.rawGrade].full}
                >
                  {order.rawGrade}
                </span>
              )}
            </>
          )}
          <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
            <Eye className="h-3 w-3" /> {order.offerCount} {order.offerCount === 1 ? "offer" : "offers"}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-muted-foreground" title={order.preferredShipping}>
            <Truck className="h-3 w-3" /> {order.preferredShipping.replace("Standard ", "Std ").replace("Express ", "Exp ")}
          </span>
          {order.signatureRequired && (
            <span className="inline-flex items-center gap-1 rounded bg-warning/15 px-1.5 py-0.5 text-warning">
              <PenLine className="h-3 w-3" /> Sig
            </span>
          )}
        </div>
      )}

      <div className="mt-auto flex items-end justify-between border-t border-border pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {isSold ? "Final sale" : "Target"}
          </div>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <Price usd={isSold && order.finalPrice != null ? order.finalPrice : order.targetPrice} size="md" />
          </div>
          {!isSold && order.buyerCity && (
            <div className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
              <MapPin className="h-2.5 w-2.5" /> {order.buyerCity}
            </div>
          )}
          {!isSold && order.condition === "graded" && order.altGrades && order.altGrades.length > 0 && (
            <div className="mt-0.5 text-[10px] text-slate-400">
              Also accepts: {order.altGrades.map(g => `${gradeLabel(g.gradingCo, g.minGrade, g.variant)}+`).join(", ")}
            </div>
          )}
          {!isSold && order.marketValue != null && (
            <div
              className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground"
              title="Estimated market value"
            >
              <TrendingUp className="h-2.5 w-2.5 text-primary" />
              Market <span className="text-foreground/80 font-semibold">${order.marketValue.toLocaleString()}</span>
            </div>
          )}
        </div>
        <div className="text-right text-[10px] text-muted-foreground">
          {isSold ? (
            <div>Completed {order.completedAt}</div>
          ) : (
            <>
              {order.buyer && (
                <div className="flex items-center justify-end gap-1 font-semibold text-foreground/90">
                  <span>@{order.buyer}</span>
                </div>
              )}
              {order.buyerRating != null && order.buyerReviews != null && order.buyerReviews > 0 && (
                <div className="flex items-center justify-end gap-0.5">
                  <Star className="h-2.5 w-2.5 fill-warning text-warning" />
                  <span className="text-foreground/80">{order.buyerRating.toFixed(1)}</span>
                  <span>({order.buyerReviews})</span>
                  <BadgeCheck
                    className="ml-0.5 h-2.5 w-2.5 text-primary"
                    aria-label="Mutual verified references"
                  />
                </div>
              )}
              <div>{order.createdAt}</div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export { CardArt };
